import { getKnownCardAnchors, MAIN_CONFIG_TAB_KEYS } from '../screens/webshop/search/WebshopConfigSearch';

// Dynamic permission CATALOG, derived from artifacts the codebase already
// maintains (the search index) so it auto-grows: add a card anchor / search
// route and it becomes a gateable node here + in the role-config tree, with no
// second hand-maintained list.
//
// Node hierarchy (parentKey links): screen → main config tab → card. Plus
// opt-in field nodes. `can()` (in Permissions.js) uses the parent/child maps;
// labels (needing `t`) are only built for the dialog.
//
// Key scheme (kept compatible with the existing call sites):
//   webshop.screen.<x>            screens (sidebar entries)
//   webshop.config.tab.<x>        main config tabs
//   webshop.config.card.<tail>    a card; <tail> = anchor minus "cfg-card-"
//   webshop.field.<x>             opt-in individual field

const SCREEN_NODES = [
    { key: 'webshop.screen.config',      label: 'Configuratie' },
    { key: 'webshop.screen.data',        label: 'Data' },
    { key: 'webshop.screen.connections', label: 'Connecties' },
    { key: 'webshop.screen.actions',     label: 'Acties' },
];

// Main config tabs (under the Configuratie screen). Index = main tab index, so
// `getKnownCardAnchors().main` maps a card onto its tab. Labels reuse the SAME
// i18n keys the tabs themselves use (MAIN_CONFIG_TAB_KEYS).
const MAIN_TAB_KEYS = [
    'webshop.config.tab.pages',
    'webshop.config.tab.style',
    'webshop.config.tab.features',
    'webshop.config.tab.processing',
    'webshop.config.tab.sync',
];
const MAIN_TAB_FALLBACK = ['Pages', 'Style', 'Features', 'Processing', 'Sync'];

// Opt-in field nodes — the framework supports field-level gating everywhere;
// these are the explicitly-wired ones. Add an entry + a can() call site to gate
// a new field.
const FIELD_NODES = [
    { key: 'webshop.field.dbCredentials', parent: 'webshop.screen.connections', label: 'Database-credentials (Connecties)' },
];

export const cardKeyForAnchor = (anchor) =>
    anchor ? 'webshop.config.card.' + String(anchor).replace(/^cfg-card-/, '') : null;

// Last segment of an "A › B › C" crumb — a concise card label.
const lastSegment = (crumb) => {
    const parts = String(crumb || '').split('›').map(s => s.trim()).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : String(crumb || '');
};

// ── Static structure (label-independent), computed once at module load ───────
const CARD_ANCHORS = getKnownCardAnchors();

export const CATALOG_PARENTS = (() => {
    const parents = {};
    SCREEN_NODES.forEach(s => { parents[s.key] = null; });
    MAIN_TAB_KEYS.forEach(k => { parents[k] = 'webshop.screen.config'; });
    CARD_ANCHORS.forEach(c => {
        parents[cardKeyForAnchor(c.anchor)] = MAIN_TAB_KEYS[c.main] || 'webshop.screen.config';
    });
    FIELD_NODES.forEach(f => { parents[f.key] = f.parent; });
    return parents;
})();

export const ALL_CATALOG_KEYS = Object.keys(CATALOG_PARENTS);

export const CATALOG_CHILDREN = (() => {
    const children = {};
    Object.entries(CATALOG_PARENTS).forEach(([key, parent]) => {
        if (parent) (children[parent] = children[parent] || []).push(key);
    });
    return children;
})();

// All descendant keys of a key (depth-first).
export const descendantsOf = (key) => {
    const out = [];
    const stack = [...(CATALOG_CHILDREN[key] || [])];
    while (stack.length) {
        const k = stack.pop();
        out.push(k);
        (CATALOG_CHILDREN[k] || []).forEach(c => stack.push(c));
    }
    return out;
};

// A node's own key + all its descendants — handy for granting a whole branch.
export const subtreeKeys = (key) => [key, ...descendantsOf(key)];

// ── Labelled nodes for the dialog (needs `t`) ────────────────────────────────
export const buildCatalogNodes = (t) => {
    const tr = (key, fb) => {
        if (typeof t !== 'function' || !key) return fb;
        const v = t(key);
        return (v && v !== key) ? v : fb;
    };
    const nodes = [];
    SCREEN_NODES.forEach(s => nodes.push({ key: s.key, label: s.label, parentKey: null, kind: 'screen' }));
    MAIN_TAB_KEYS.forEach((k, i) => nodes.push({
        key: k, label: tr(MAIN_CONFIG_TAB_KEYS[i], MAIN_TAB_FALLBACK[i]),
        parentKey: 'webshop.screen.config', kind: 'tab',
    }));
    CARD_ANCHORS.forEach(c => nodes.push({
        key: cardKeyForAnchor(c.anchor), label: lastSegment(c.crumb), crumb: c.crumb,
        parentKey: MAIN_TAB_KEYS[c.main] || 'webshop.screen.config', kind: 'card',
    }));
    FIELD_NODES.forEach(f => nodes.push({ key: f.key, label: f.label, parentKey: f.parent, kind: 'field' }));
    return nodes;
};
